import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  devices: [],
  deviceStatuses: [],
  connectionStatuses: [], // Connection statuses from init endpoint
  deviceTypes: [], // Device types from init endpoint
  verifyModes: [], // Verify modes from init endpoint
  availableUnits: [], // Units from init endpoint for filter
  initDevice: null,
  canReview: false,
  units: [], // Units for assign modal
  unitsTotal: 0,
  unitsPage: 1,
  unitsPageSize: 10,
  unitsLoading: false,
  total: 0,
  page: 1,
  pageSize: 10,
  sortColumn: 'deviceCode',
  sortDirection: 'asc',
  filterCode: '',
  filterStatuses: [], // Multi-select status filter
  filterUnits: [], // Multi-select unit filter
  loading: false,
  deviceDetailLoading: false, // Separate loading state for fetching single device
  exportLoading: false, // Loading state for export operations
  error: null,
  successMessage: null,
  selectedDevice: null,
};

const devicesSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    // Fetch init data
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.deviceStatuses = action.payload.deviceStatuses || [];
      state.connectionStatuses = action.payload.connectionStatuses || [];
      state.deviceTypes = action.payload.deviceTypes || [];
      state.verifyModes = action.payload.verifyModes || [];
      state.availableUnits = action.payload.units || [];
      state.initDevice = action.payload.initDevice || null;
      state.canReview = action.payload.canReview || false;
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch devices
    fetchDevices: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchDevicesSuccess: (state, action) => {
      state.loading = false;
      state.devices = action.payload.data;
      state.total = action.payload.meta.total;
      state.error = null;
    },
    fetchDevicesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch device by ID
    fetchDeviceById: (state) => {
      state.deviceDetailLoading = true;
      state.error = null;
    },
    fetchDeviceByIdSuccess: (state, action) => {
      state.deviceDetailLoading = false;
      state.selectedDevice = action.payload;
      state.error = null;
    },
    fetchDeviceByIdFailure: (state, action) => {
      state.deviceDetailLoading = false;
      state.error = action.payload;
    },

    // Add device
    addDevice: (state) => {
      state.loading = true;
      state.error = null;
    },
    addDeviceSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    addDeviceFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update device
    updateDevice: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateDeviceSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    updateDeviceFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Delete device
    deleteDevice: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteDeviceSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    deleteDeviceFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Bulk delete
    bulkDeleteDevices: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkDeleteDevicesSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    bulkDeleteDevicesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1;
    },

    // Sorting
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },

    // Filtering
    setFilterCode: (state, action) => {
      state.filterCode = action.payload;
      state.page = 1;
    },
    setFilterStatuses: (state, action) => {
      state.filterStatuses = action.payload;
      state.page = 1;
    },
    setFilterUnits: (state, action) => {
      state.filterUnits = action.payload;
      state.page = 1;
    },

    // Review actions
    approveDevice: (state) => {
      state.loading = true;
      state.error = null;
    },
    approveDeviceSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message || 'devices.success.approved';
      state.error = null;
    },
    approveDeviceFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    rejectDevice: (state) => {
      state.loading = true;
      state.error = null;
    },
    rejectDeviceSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    rejectDeviceFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    revokeDevice: (state) => {
      state.loading = true;
      state.error = null;
    },
    revokeDeviceSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message || 'devices.success.revoked';
      state.error = null;
    },
    revokeDeviceFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Toggle LGSP Access
    toggleLGSPAccess: (state) => {
      state.loading = true;
      state.error = null;
    },
    toggleLGSPAccessSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    toggleLGSPAccessFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update Connection Status
    updateConnectionStatus: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateConnectionStatusSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    updateConnectionStatusFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Verify Citizen
    verifyCitizen: (state) => {
      state.loading = true;
      state.error = null;
    },
    verifyCitizenSuccess: (state, action) => {
      state.loading = false;
      state.error = null;
    },
    verifyCitizenFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch units for assignment
    fetchUnits: (state) => {
      state.unitsLoading = true;
      state.error = null;
    },
    fetchUnitsSuccess: (state, action) => {
      state.unitsLoading = false;
      state.units = action.payload.data;
      state.unitsTotal = action.payload.meta.total;
      state.error = null;
    },
    fetchUnitsFailure: (state, action) => {
      state.unitsLoading = false;
      state.error = action.payload;
    },

    // Assign unit to device
    assignUnit: (state) => {
      state.loading = true;
      state.error = null;
    },
    assignUnitSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    assignUnitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Set units pagination
    setUnitsPage: (state, action) => {
      state.unitsPage = action.payload;
    },
    setUnitsPageSize: (state, action) => {
      state.unitsPageSize = action.payload;
      state.unitsPage = 1;
    },

    // Import devices
    importDevices: (state) => {
      state.loading = true;
      state.error = null;
    },
    importDevicesSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    importDevicesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Export devices
    exportDevices: (state) => {
      state.exportLoading = true;
      state.error = null;
    },
    exportDevicesSuccess: (state) => {
      state.exportLoading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    exportDevicesFailure: (state, action) => {
      state.exportLoading = false;
      state.error = action.payload;
    },

    // Download template
    downloadTemplate: (state) => {
      state.exportLoading = true;
      state.error = null;
    },
    downloadTemplateSuccess: (state) => {
      state.exportLoading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    downloadTemplateFailure: (state, action) => {
      state.exportLoading = false;
      state.error = action.payload || 'devices.errors.downloadTemplateFailed';
    },

    // Bulk re-approve devices
    bulkReapproveDevices: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkReapproveDevicesSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    bulkReapproveDevicesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Clear messages
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearSelectedDevice: (state) => {
      state.selectedDevice = null;
    },
  },
});

export const {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchDevices,
  fetchDevicesSuccess,
  fetchDevicesFailure,
  fetchDeviceById,
  fetchDeviceByIdSuccess,
  fetchDeviceByIdFailure,
  addDevice,
  addDeviceSuccess,
  addDeviceFailure,
  updateDevice,
  updateDeviceSuccess,
  updateDeviceFailure,
  deleteDevice,
  deleteDeviceSuccess,
  deleteDeviceFailure,
  bulkDeleteDevices,
  bulkDeleteDevicesSuccess,
  bulkDeleteDevicesFailure,
  approveDevice,
  approveDeviceSuccess,
  approveDeviceFailure,
  rejectDevice,
  rejectDeviceSuccess,
  rejectDeviceFailure,
  revokeDevice,
  revokeDeviceSuccess,
  revokeDeviceFailure,
  toggleLGSPAccess,
  toggleLGSPAccessSuccess,
  toggleLGSPAccessFailure,
  updateConnectionStatus,
  updateConnectionStatusSuccess,
  updateConnectionStatusFailure,
  verifyCitizen,
  verifyCitizenSuccess,
  verifyCitizenFailure,
  fetchUnits,
  fetchUnitsSuccess,
  fetchUnitsFailure,
  assignUnit,
  assignUnitSuccess,
  assignUnitFailure,
  setUnitsPage,
  setUnitsPageSize,
  importDevices,
  importDevicesSuccess,
  importDevicesFailure,
  exportDevices,
  exportDevicesSuccess,
  exportDevicesFailure,
  downloadTemplate,
  downloadTemplateSuccess,
  downloadTemplateFailure,
  bulkReapproveDevices,
  bulkReapproveDevicesSuccess,
  bulkReapproveDevicesFailure,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterCode,
  setFilterStatuses,
  setFilterUnits,
  clearError,
  clearSuccessMessage,
  clearSelectedDevice,
} = devicesSlice.actions;

// Selectors
export const selectDevices = (state) => state.devices?.devices || [];
export const selectDeviceStatuses = (state) => state.devices?.deviceStatuses || [];
export const selectConnectionStatuses = (state) => state.devices?.connectionStatuses || [];
export const selectDeviceTypes = (state) => state.devices?.deviceTypes || [];
export const selectVerifyModes = (state) => state.devices?.verifyModes || [];
export const selectAvailableUnits = (state) => state.devices?.availableUnits || [];
export const selectInitDevice = (state) => state.devices?.initDevice;
export const selectCanReview = (state) => state.devices?.canReview || false;
export const selectSelectedDevice = (state) => state.devices?.selectedDevice;
export const selectDevicesLoading = (state) => state.devices?.loading || false;
export const selectDevicesError = (state) => state.devices?.error;
export const selectDevicesSuccessMessage = (state) => state.devices?.successMessage;
export const selectDevicesTotal = (state) => state.devices?.total || 0;
export const selectDevicesPage = (state) => state.devices?.page || 1;
export const selectDevicesPageSize = (state) => state.devices?.pageSize || 10;
export const selectDevicesSortColumn = (state) => state.devices?.sortColumn || 'deviceCode';
export const selectDevicesSortDirection = (state) => state.devices?.sortDirection || 'asc';
export const selectDevicesFilterCode = (state) => state.devices?.filterCode || '';
export const selectDevicesFilterStatuses = (state) => state.devices?.filterStatuses || [];
export const selectDevicesFilterUnits = (state) => state.devices?.filterUnits || [];
export const selectUnits = (state) => state.devices?.units || [];
export const selectUnitsTotal = (state) => state.devices?.unitsTotal || 0;
export const selectUnitsPage = (state) => state.devices?.unitsPage || 1;
export const selectUnitsPageSize = (state) => state.devices?.unitsPageSize || 10;
export const selectUnitsLoading = (state) => state.devices?.unitsLoading || false;
export const selectExportLoading = (state) => state.devices?.exportLoading || false;

export default devicesSlice.reducer;
